/**
 * Form Language Helper Functions
 * Provides utilities for getting localized form text based on user's preferred language
 */

import { useAuth } from '@/contexts/AuthContext';
import { Form, FormField, TableItemsDefinition } from '@/firebase/formService';

/**
 * Get user's preferred language
 */
export function getUserPreferredLanguage(user: { preferredLanguage?: string } | null | undefined): string {
  return user?.preferredLanguage === 'es' ? 'es' : 'en';
}

/**
 * Get localized form name
 */
export function getFormName(form: Form, preferredLanguage: string): string {
  if (preferredLanguage === 'es' && form.name_es) {
    return form.name_es;
  }
  return form.name;
}

/**
 * Get localized field label
 */
export function getFieldLabel(field: FormField, preferredLanguage: string): string {
  if (preferredLanguage === 'es' && field.label_es) {
    return field.label_es;
  }
  return field.label;
}

/**
 * Get localized item names for table forms
 */
export function getItemNames(itemsDefinition: TableItemsDefinition | undefined, preferredLanguage: string): string[] {
  if (!itemsDefinition || !itemsDefinition.items) {
    return [];
  }

  if (preferredLanguage === 'es' && itemsDefinition.items_es && itemsDefinition.items_es.length === itemsDefinition.items.length) {
    return itemsDefinition.items_es;
  }

  return itemsDefinition.items;
}

/**
 * Get localized form description
 */
export function getFormDescription(form: Form, preferredLanguage: string): string | undefined {
  if (preferredLanguage === 'es' && form.description_es) {
    return form.description_es;
  }
  return form.description;
}

/**
 * Get field label mapping for form completions
 * Maps English labels (used as keys in fieldValues) to display labels
 */
export function getFieldLabelMapping(form: Form, preferredLanguage: string): Record<string, string> {
  const mapping: Record<string, string> = {};
  
  // Standard fields - map English label (key in fieldValues) to display label
  form.fields?.forEach(field => {
    const englishLabel = field.label; // e.g., "Date of Service"
    const displayLabel = getFieldLabel(field, preferredLanguage); // e.g., "Fecha del Servicio" or "Date of Service"
    mapping[englishLabel] = displayLabel;
  });
  
  // Table fields
  form.tableFields?.forEach(field => {
    const englishLabel = field.label; // e.g., "Current Inventory"
    const displayLabel = getFieldLabel(field, preferredLanguage); // e.g., "Inventario Actual" or "Current Inventory"
    mapping[englishLabel] = displayLabel;
  });
  
  // Header fields
  form.headerFields?.forEach(field => {
    const englishLabel = field.label;
    const displayLabel = getFieldLabel(field, preferredLanguage);
    mapping[englishLabel] = displayLabel;
  });
  
  return mapping;
}

